import type {BaseApiErrorRecovery, Middleware, ResponseContext} from '@linx-security/api-specs';
import {ApiErrorType} from '@linx-security/api-specs';
import type {Logger} from 'pino';

import {ApiError, HTTPError, isErrorType} from '../types';

import {baseLogger} from './log-middleware';
import {safeParseResponseBody} from './parse-response-body';

/**
 * Middleware to handle API errors and capture them using Sentry for tracking.
 *
 * The middleware distinguishes between two types of errors:
 *
 * 1. Intended errors: These are errors the Backend (BE) is aware of and
 *    sends to the Frontend (FE) in a predetermined structure. They are
 *    captured as messages in Sentry.
 *
 * 2. Unintended errors: These are errors that the BE is not aware of.
 *    They are captured as exceptions in Sentry.
 *
 * In both cases, errors are cast to ApiError so that they can be uniformly
 * handled by the FE.
 *
 * @param {Logger} logger - The logger instance, defaulting to `baseLogger`.
 * @returns {Middleware} - The middleware object with `onError` and `post` hooks.
 */
export const getCastErrorMiddleware: (logger: Logger) => Middleware = (logger = baseLogger) => ({
  /**
   * Handles issues with the fetching process itself, such as network errors.
   *
   * @param {ResponseContext} context - The fetch response context.
   * @returns {Response} - Returns the original fetch Response object.
   */
  onError: async context => {
    baseLogger.error({error: context.error, response: context.response}, (context.error as any)?.message);
    console.error(`API fetch error`, {
      error: context.error,
      url: context.url,
      method: context.init?.method,
    });
    return context.response;
  },

  /**
   * Post-fetch middleware to handle and cast errors based on their type.
   *
   * @param {ResponseContext} context - The fetch response context.
   * @returns {Promise<Response>} - Returns either the original or modified fetch Response object.
   */
  post: async (context: ResponseContext): Promise<Response> => {
    const {response, init} = context;

    // If status code is below 400, it's a success, return the original response.
    if (response.status < 400) {
      return response;
    }

    let body: any;

    // Parse the response body if it's a JSON.
    const textResponses = [`application/problem+json`, `application/json`, `text/plain; charset=utf-8`] as const;
    // TODO: remove this after (PRE-2427)[https://linear.app/linxsecurity/issue/PRE-2427/[bug]-error-response-isnt-returned-in-the-expected-structure] is done
    if (textResponses.includes(response.headers.get(`Content-Type`) as (typeof textResponses)[number])) {
      body = await safeParseResponseBody(logger, response);
    }
    // Determine the type of error and any recovery information.
    const errorType: ApiErrorType | undefined = body?.error?.type;
    const recovery: BaseApiErrorRecovery | undefined = body?.error?.recovery;

    // If the error type is unknown, it's an unintended error.
    if (!isErrorType(errorType)) {
      const {status, statusText, url} = context.response;
      const {method} = context.init;
      const isTransient = status === 502 || status === 503 || status === 504;

      if (isTransient) {
        baseLogger.warn({status, method, url: context.url}, `Transient API error`);
        console.warn(`Transient API error`, {
          url: context.url,
          method: context.init.method,
          status,
          isIntended: false,
        });
      } else {
        baseLogger.error({response: context.response}, `API Response ${context.url}`);
        const e = new HTTPError({message: statusText, method, status, url, body});
        console.error(`Unhandled API error`, {
          error: e,
          url: context.url,
          method: context.init.method,
          isIntended: false,
        });
      }

      // Throw the error as casted ApiError since FE is aware only of ApiError
      throw new ApiError({
        message: `${statusText} ${body}`,
        method,
        status,
        url,
        type: ApiErrorType.InternalServerError,
        recovery,
        body,
      });
    }

    // If error is a client error (400-499), it's an intended error.
    const {status, statusText, url} = response;
    const {method} = init;

    const e = new ApiError({
      message: statusText,
      method,
      status,
      url,
      type: errorType,
      recovery,
    });

    console.warn(`API error`, {
      message: e.message,
      error: e,
      isIntended: true,
    });

    throw e;
  },
});
